import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  listDiscountOptions,
  createDiscountOption,
  updateDiscountOption,
  deleteDiscountOption
} from '../../api/discountOptionsPortalApi';
import { listCampuses } from '../../api/campusPortalApi';
import * as S from '../../pages/Portal/Management.styles';

const DiscountOptionsManager = () => {
  const auth = useAuth();
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks
  const isDeveloper = portalGroups.includes('portal_developer');
  const isDirector = portalGroups.includes('portal_director');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isManager = portalGroups.includes('portal_manager');
  const canManage = isDeveloper || isDirector || isSystemAdmin || isManager;
  const canViewAll = isDeveloper || isSystemAdmin;

  // Data states
  const [discountOptions, setDiscountOptions] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [filterCampus, setFilterCampus] = useState('all');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: '',
    campusId: 'global'
  });
  const [saving, setSaving] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [optionsData, campusesData] = await Promise.all([
        listDiscountOptions({ showInactive: canViewAll }),
        listCampuses()
      ]);
      setDiscountOptions(optionsData);
      setCampuses(campusesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canViewAll]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter options
  const filteredOptions = discountOptions.filter(opt => {
    if (filterCampus === 'all') return true;
    if (filterCampus === 'global') return opt.campusId === 'global';
    return opt.campusId === filterCampus || opt.campusId === 'global';
  });

  // Get campus name
  const getCampusName = (campusId) => {
    if (campusId === 'global') return 'Global';
    const campus = campuses.find(c => c.id === campusId);
    return campus ? campus.name : campusId;
  };

  // Open create modal
  const handleCreate = () => {
    setEditingOption(null);
    setFormData({
      name: '',
      description: '',
      value: '',
      campusId: 'global'
    });
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (option) => {
    setEditingOption(option);
    setFormData({
      name: option.name,
      description: option.description || '',
      value: option.value.toString(),
      campusId: option.campusId
    });
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOption(null);
    setFormData({
      name: '',
      description: '',
      value: '',
      campusId: 'global'
    });
  };

  // Save discount option
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }
    if (!formData.value || isNaN(formData.value) || formData.value < 0 || formData.value > 100) {
      alert('Please enter a valid discount value (0-100)');
      return;
    }

    setSaving(true);
    try {
      if (editingOption) {
        await updateDiscountOption(editingOption.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          value: parseFloat(formData.value),
          campusId: formData.campusId
        });
      } else {
        await createDiscountOption({
          name: formData.name.trim(),
          description: formData.description.trim(),
          value: parseFloat(formData.value),
          campusId: formData.campusId
        });
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      alert('Error saving discount option: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete discount option
  const handleDelete = async (option) => {
    if (!window.confirm(`Are you sure you want to delete "${option.name}"?`)) {
      return;
    }

    try {
      await deleteDiscountOption(option.id);
      fetchData();
    } catch (err) {
      alert('Error deleting discount option: ' + err.message);
    }
  };

  // Reactivate discount option
  const handleReactivate = async (option) => {
    try {
      await updateDiscountOption(option.id, { isActive: true });
      fetchData();
    } catch (err) {
      alert('Error reactivating discount option: ' + err.message);
    }
  };

  if (auth.isLoading || loading) {
    return (
      <S.Card>
        <S.CardHeader>
          <h3>Discount Options</h3>
        </S.CardHeader>
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          Loading...
        </div>
      </S.Card>
    );
  }

  if (!canManage) {
    return (
      <S.Card>
        <S.CardHeader>
          <h3>Discount Options</h3>
        </S.CardHeader>
        <S.ErrorBanner style={{ margin: '16px' }}>
          You do not have permission to manage discount options.
        </S.ErrorBanner>
      </S.Card>
    );
  }

  return (
    <>
      <S.Card>
        <S.CardHeader>
          <h3>Discount Options</h3>
          <S.SmallButton onClick={handleCreate}>+ Add Discount</S.SmallButton>
        </S.CardHeader>

        {error && (
          <S.ErrorBanner style={{ margin: '16px' }}>
            Error: {error}
          </S.ErrorBanner>
        )}

        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <S.FilterSelect
            value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}
          >
            <option value="all">All Campuses</option>
            <option value="global">Global Only</option>
            {campuses.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </S.FilterSelect>
        </div>

        <S.TableContainer style={{ boxShadow: 'none' }}>
          <S.Table>
            <thead>
              <tr>
                <S.Th>Name</S.Th>
                <S.Th>Value</S.Th>
                <S.Th>Campus</S.Th>
                <S.Th>Status</S.Th>
                <S.Th>Actions</S.Th>
              </tr>
            </thead>
            <tbody>
              {filteredOptions.length === 0 ? (
                <tr>
                  <S.Td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>
                    No discount options found
                  </S.Td>
                </tr>
              ) : (
                filteredOptions.map(option => (
                  <tr key={option.id} style={{ opacity: option.isActive ? 1 : 0.5 }}>
                    <S.Td>
                      <S.UserName>{option.name}</S.UserName>
                      {option.description && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          {option.description}
                        </div>
                      )}
                    </S.Td>
                    <S.Td>
                      <S.Badge color="#F1AF59">{option.value}%</S.Badge>
                    </S.Td>
                    <S.Td>
                      <S.Badge color={option.campusId === 'global' ? '#3b82f6' : '#10b981'}>
                        {getCampusName(option.campusId)}
                      </S.Badge>
                    </S.Td>
                    <S.Td>
                      <S.Badge color={option.isActive ? '#10b981' : '#6b7280'}>
                        {option.isActive ? 'Active' : 'Inactive'}
                      </S.Badge>
                    </S.Td>
                    <S.Td>
                      <S.ActionButtons>
                        <S.ActionButton color="#3b82f6" onClick={() => handleEdit(option)}>
                          Edit
                        </S.ActionButton>
                        {option.isActive ? (
                          <S.ActionButton color="#ef4444" onClick={() => handleDelete(option)}>
                            Delete
                          </S.ActionButton>
                        ) : (
                          <S.ActionButton color="#10b981" onClick={() => handleReactivate(option)}>
                            Reactivate
                          </S.ActionButton>
                        )}
                      </S.ActionButtons>
                    </S.Td>
                  </tr>
                ))
              )}
            </tbody>
          </S.Table>
        </S.TableContainer>

        <S.Stats>
          Showing {filteredOptions.length} of {discountOptions.length} discount options
        </S.Stats>
      </S.Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h3>{editingOption ? 'Edit Discount Option' : 'Create Discount Option'}</h3>
              <S.CloseButton onClick={handleCloseModal}>&times;</S.CloseButton>
            </S.ModalHeader>

            <S.ModalContent>
              <S.FormGroup>
                <label>Name *</label>
                <S.Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sibling Discount"
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Description</label>
                <S.TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Discount Value (%) *</label>
                <S.Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="e.g., 10"
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Campus</label>
                <S.FilterSelect
                  value={formData.campusId}
                  onChange={(e) => setFormData({ ...formData, campusId: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="global">Global (All Campuses)</option>
                  {campuses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              <S.Note>
                <strong>Global discounts</strong> are available for all campuses.
                <br />
                <strong>Campus-specific discounts</strong> are only available when adding students to courses in that campus.
              </S.Note>
            </S.ModalContent>

            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={handleCloseModal} disabled={saving}>
                Cancel
              </S.Button>
              <S.Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editingOption ? 'Update' : 'Create')}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}
    </>
  );
};

export default DiscountOptionsManager;
